import { useState, useEffect, type FC } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { useOperationsDashboard } from './dashboard/hooks/useOperationsDashboard'
import { useDashboardFinalCii } from './dashboard/hooks/useDashboardFinalCii'
import type { ModalType, CardMeta, SummaryMetric } from './dashboard/types'
import KpiCard from './dashboard/KpiCard'
import { dashboardCardVisibility } from './dashboard/cardVisibility'
import VesselStatusTable from './dashboard/VesselStatusTable'
import VoyageTimeline from './dashboard/VoyageTimeline'
import WorkQueue from './dashboard/WorkQueue'
import CargoOperations from './dashboard/CargoOperations'
import DprSummary from './dashboard/DprSummary'
import CiiPanel from './dashboard/CiiPanel'
import DefectsOverview from './dashboard/DefectsOverview'
import ConsumptionTable from './dashboard/ConsumptionTable'
import CriticalEvents from './dashboard/CriticalEvents'
import InsightsPanel from './dashboard/InsightsPanel'
import {
  VoyageDetailModal,
  PortConsumptionModal,
  DefectDetailModal,
  DprDetailModal,
  CargoDetailModal,
  WorkQueueModal,
  CriticalEventsModal,
  InsightsModal,
  KpiDetailModal,
} from './dashboard/DashboardModals'
import { DashboardGridSkeleton } from '../../../shared/components/LoadingSkeletons'
import { DPR_ROUTE } from '../core/routes'
import { useOperationsAccess } from '../core/useOperationsAccess'
import { OP } from '../core/operationPermissions'
import { isOpenDashboardDefect, isOverdueDashboardDefect, dashboardPortConsumption } from './dashboard/dashboardData'

/* ──────────── KPI Card Meta ──────────── */
const kpiMeta: Record<string, CardMeta> = {
  activeVoyages: {
    description: 'Number of voyages currently marked as active across the fleet.',
    endpoint: 'GET /api/voyages (or /api/voyages/vessels/{id})',
    logic: 'Count of voyages where active = true.',
  },
  vesselsAtSea: {
    description: 'Vessels whose reported operational status is At Sea.',
    endpoint: 'GET /api/vessels/operations/{id}; GET /api/dpr',
    logic: 'Reported status, with latest submitted/reviewed DPR port/sea as fallback. Unknown statuses are not inferred from an active voyage.',
  },
  vesselsInPort: {
    description: 'Vessels reported In Port; anchorage is counted separately.',
    endpoint: 'GET /api/vessels/operations/{id}; GET /api/dpr',
    logic: 'Counts normalized reported In Port status in the selected vessel scope.',
  },
  openDefects: {
    description: 'Active submitted defects, excluding Drafts, closed and office-reviewed records.',
    endpoint: 'GET /api/qhse/defects (activeOnly=true)',
    logic: 'Active SUBMIT, PENDING_APPROVAL, APPROVED and REJECTED records in the selected vessel scope.',
  },
  ciiRating: {
    description: 'Ship Data final calculated rating for the selected vessel and reporting year.',
    endpoint: 'GET /api/reports/submitted-full; POST /api/cii-voyage-rows/by-report-ids; GET /api/cii-rules/rule-set',
    logic: 'Annual corrected CO2 divided by annual corrected distance, vessel capacity and correction factors; the same calculation and boundaries as g) Final Calculated Rating. Saved inputs only.',
  },
  draftDprReports: {
    description: 'Saved Daily Position Reports in Draft status for the selected vessel scope.',
    endpoint: 'GET /api/dpr',
    logic: 'Counts only daily position reports where status = DRAFT, across all saved dates. Position-report drafts and defects are separate.',
  },
}

const OperationsDashboard: FC = () => {
  const {
    loading, data, kpis, vesselStatusRows, activeTab, setActiveTab, refresh, errors, sourceError, fetchedAt,
  } = useOperationsDashboard()
  const [ciiYear, setCiiYear] = useState(new Date().getFullYear())
  const finalCii = useDashboardFinalCii(data, loading, errors, ciiYear)

  const { opCan, permissionsLoaded } = useOperationsAccess()

  /* ─── Modal State ─── */
  const [activeModal, setActiveModal] = useState<ModalType>(null)
  useEffect(() => { if (loading) setActiveModal(null) }, [loading])
  const [summaryDestination, setSummaryDestination] = useState<{ label: string; path: string } | undefined>()

  const [selectedVoyageId, setSelectedVoyageId] = useState<number | undefined>()
  const [defectView, setDefectView] = useState<'open' | 'overdue'>('open')
  const openModal = (modal: ModalType) => {
    setSelectedVoyageId(undefined)
    setDefectView('open')
    setSummaryDestination(undefined)
    setActiveModal(modal)
  }
  const closeModal = () => setActiveModal(null)

  /* ─── Quick Actions ─── */
  const quickActions = [
    { label: 'Create Voyage', icon: '/media/map/zoom-in.svg', destination: { label: 'Go to Voyages', path: '/operations/voyages' } },
    { label: 'Position Report', icon: '/media/icons/duotune/files/fil008.svg', destination: { label: 'Go to Position Reports', path: '/operations/overview' } },
    { label: 'Daily Position Report', icon: '/media/icons/duotune/general/gen014.svg', destination: { label: 'Go to Daily Position Reports', path: DPR_ROUTE } },
    { label: 'Cargo Operation', icon: '/media/icons/duotune/finance/fin001.svg', destination: { label: 'Go to Cargo Operations', path: '/operations/cargo-operations' } },
    { label: 'In-Port Consumption', icon: '/media/icons/duotune/maps/map002.svg', destination: { label: 'Go to In-Port Consumption', path: '/operations/inport-consumption' } },
    { label: 'Library / Templates', icon: '/media/icons/duotune/coding/cod001.svg', destination: { label: 'Go to Library / Templates', path: '/operations/library' } },
    { label: 'Defects List', icon: '/media/icons/duotune/abstract/abs042.svg', destination: { label: 'Go to Defects List', path: '/operations/defects-list' } },
  ]

  const portConsumption = dashboardPortConsumption(data.dprList, data.vessels)
  const activeVoyages = data.voyages.filter(v => v.active)
  const activeVessels = new Set(activeVoyages.map(v => v.vessel?.id))
  const statusChartVessels = data.vessels.filter(v => activeVessels.has(v.id))
  const draftDates = Array.from({ length: 7 }, (_, i) => new Date(Date.now() - (6 - i) * 86400000).toISOString().slice(0, 10))

  /* ─── Insights data ─── */
  const insights = [
    ...(!sourceError('voyages') ? [{ icon: 'trending-up', color: 'success', text: `${kpis.activeVoyages} active voyages across ${new Set(data.voyages.filter(v => v.active).map(v => v.vessel?.id)).size} vessels` }] : []),
    ...(!sourceError('defects') ? [{ icon: 'alert-circle', color: 'warning', text: `${kpis.openDefects} open defect reports; ${kpis.overdueDefects} observed more than 30 days ago. Drafts excluded.` }] : []),
    ...(!sourceError('reports') && kpis.overdueReports > 0 ? [{ icon: 'clock', color: 'orange', text: `${kpis.overdueReports} saved Draft position reports are older than 24 hours (age, not a submission deadline)` }] : []),
    ...(!sourceError('status') ? [{ icon: 'clock', color: 'primary', text: `${kpis.vesselsInPort} active-voyage vessels reported In Port; ${kpis.vesselsUnknown} statuses unknown` }] : []),
    ...(Object.keys(errors).length > 0 ? [{ icon: 'alert-circle', color: 'warning', text: 'Some sources are unavailable. Insights include successfully loaded sources only.' }] : []),
  ]
  const metric = (label: string, value: number | string, source: string, detail?: string): SummaryMetric => ({
    label, value: loading ? 'Loading…' : sourceError(source) ? 'Unavailable' : value,
    detail: sourceError(source) || detail,
  })
  const summaryMetrics: SummaryMetric[] = [
    metric('Active Voyages', kpis.activeVoyages, 'voyages'),
    metric('At Sea', kpis.vesselsAtSea, 'status', `${kpis.vesselsUnknown} vessel statuses unknown`),
    metric('In Port', kpis.vesselsInPort, 'status', `${kpis.vesselsAtAnchor} at anchorage (separate)`),
    metric('Open Defects', kpis.openDefects, 'defects', 'Excludes Drafts, closed and office-reviewed defects'),
    { label: 'Final Calculated Rating', value: loading || finalCii.loading ? 'Loading…' : finalCii.error || sourceError('cii') ? 'Unavailable' : finalCii.needsVessel ? 'Select a vessel' : finalCii.result?.rating || 'Not available',
      detail: finalCii.error || sourceError('cii') || finalCii.result?.reason || (finalCii.result ? `${finalCii.result.vesselName} · ${ciiYear}${finalCii.result.asOf ? ` · Reports through ${finalCii.result.asOf.slice(0, 10)}` : ''}` : undefined) },
    metric('DPR Reports', kpis.draftDprReports, 'dpr', 'Draft Daily Position Reports only; all saved dates'),
  ]
  let shortcutMetrics = summaryMetrics
  switch (summaryDestination?.path) {
    case '/operations/voyages':
      shortcutMetrics = [metric('Voyages', kpis.totalVoyages, 'voyages'), summaryMetrics[0], metric('Inactive Voyages', kpis.totalVoyages - kpis.activeVoyages, 'voyages')]
      break
    case '/operations/overview':
      shortcutMetrics = [metric('Position Reports', data.createdReports.length, 'reports'), metric('Draft Reports', data.createdReports.filter(r => r.status === 'DRAFT').length, 'reports'), metric('Submitted Reports', data.createdReports.filter(r => r.status === 'SUBMITTED').length, 'reports')]
      break
    case DPR_ROUTE:
      shortcutMetrics = [metric('Daily Position Reports', data.dprList.length, 'dpr'), metric('Drafts', data.dprList.filter(d => d.status === 'DRAFT').length, 'dpr'), metric('Submitted / Reviewed Today (UTC)', kpis.totalDprToday, 'dpr')]
      break
    case '/operations/cargo-operations':
      shortcutMetrics = [metric('Cargo Records', data.cargoOps.length, 'cargo', 'All pages; not an active-status count'), metric('Tank Records', kpis.tankCargoCount, 'cargo'), metric('Hold Records', kpis.holdCargoCount, 'cargo')]
      break
    case '/operations/inport-consumption':
      shortcutMetrics = [metric('Recorded Port Fuel Rows', portConsumption.length, 'dpr', 'Submitted/Reviewed DPR berth and anchorage rows; KL and hours'), metric('Vessels with Recorded Port Data', new Set(portConsumption.map(row => row.vesselId)).size, 'dpr'), metric('Source', 'Daily Position Reports', 'dpr', 'Individual saved rows; whole-report fuel totals are excluded')]
      break
    case '/operations/library':
      shortcutMetrics = [metric('Master Templates (Catalog)', data.templates.length, 'templates', 'Accessible master catalog, not a count of company/vessel assignments. Open Library to view scoped templates.')]
      break
    case '/operations/defects-list':
      shortcutMetrics = [metric('Active Defect Records', data.defects.length, 'defects'), summaryMetrics[3], metric('Closed / Office Reviewed', data.defects.filter(d => ['CLOSE', 'REVIEWED'].includes(d.status)).length, 'defects')]
      break
  }
  const downloadPermissions: Record<string, string[]> = {
    '/operations/voyages': [OP.DOWNLOAD_VOYAGE],
    '/operations/overview': [OP.DOWNLOAD_POSITION_REPORT],
    [DPR_ROUTE]: [OP.DOWNLOAD_DAILY_PROGRESS_REPORT],
    '/operations/cargo-operations': [OP.DOWNLOAD_CARGO_OPERATION],
    '/operations/inport-consumption': [OP.DOWNLOAD_DAILY_PROGRESS_REPORT],
    '/operations/library': ['READ_REPORT_TEMPLATE'],
    '/operations/defects-list': ['READ_QHSE_DEFECT'],
  }
  const summaryCanDownload = permissionsLoaded && (summaryDestination
    ? downloadPermissions[summaryDestination.path] ?? []
    : [OP.DOWNLOAD_VOYAGE, OP.DOWNLOAD_POSITION_REPORT, 'READ_QHSE_DEFECT', OP.READ_CII_SUMMARY, OP.DOWNLOAD_DAILY_PROGRESS_REPORT]).every(opCan)
  const sourceLabel = (source: string) => {
    const [kind, vesselId] = source.split(':')
    const labels: Record<string, string> = { vessels: 'Vessel access', voyages: 'Voyages', reports: 'Position reports', dpr: 'Daily position reports', cargo: 'Cargo records', templates: 'Template catalog', defects: 'Defect reports', status: 'Vessel status', cii: 'Recorded CII' }
    const vessel = data.vessels.find(v => String(v.id) === vesselId)
    return `${labels[kind] || kind}${vesselId ? ` — ${vessel?.fleet_name || `vessel #${vesselId}`}` : ''}`
  }
  const locationStatus = activeModal === 'vesselsAtSea' ? 'At Sea' : activeModal === 'vesselsInPort' ? 'In Port' : undefined
  const locationVoyageIds = new Set(vesselStatusRows.filter(row => row.status === locationStatus).map(row => row.voyageId))
  const detailVoyages = data.voyages.filter(v => v.active && (selectedVoyageId == null || v.id === selectedVoyageId) && (!locationStatus || locationVoyageIds.has(v.id)))
  const nowStr = fetchedAt ? fetchedAt.toLocaleString('en-GB', { timeZone: 'UTC', timeZoneName: 'short' }) : 'Not loaded'

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main' style={{ height: '100vh' }}>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid' style={{ flex: 1 }}>
          <div className='container-fluid py-4' style={{ maxWidth: '1440px' }}>
            {/* ── Header ── */}
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4'>
              <div>
                <h3 className='fw-bold text-dark mb-1'>Operations Command Center</h3>
                <span className='text-muted fs-8'>Operational risks, voyage status and actionables</span>
              </div>
              <div className='d-flex align-items-center gap-3'>
                <span className='text-muted fs-9'>Last fetch: {nowStr}</span>
                <button className='btn btn-icon btn-sm btn-light' onClick={refresh} title='Refresh' disabled={loading}>
                  <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                </button>
              </div>
            </div>

            {Object.keys(errors).length > 0 && <div className='alert alert-warning' role='alert'>
              Some sources could not be loaded. Affected metrics are unavailable.
              <ul>{Object.entries(errors).map(([source, message]) => <li key={source}>{sourceLabel(source)}: {message}</li>)}</ul>
              <button className='btn btn-sm btn-light' disabled={loading} onClick={refresh}>Retry loading data</button>
            </div>}

            {/* ── Quick Actions ── */}
            <div className='d-flex flex-wrap gap-3 mb-4 pb-3 border-bottom'>
              {quickActions.map((qa, i) => (
                <button
                  key={i}
                  className='btn btn-light d-flex align-items-center gap-2 px-3 py-2'
                  style={{ borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}
                  onClick={() => {
                    setSummaryDestination(qa.destination)
                    setActiveModal('kpiDetail')
                  }}
                >
                  <KTSVG path={qa.icon} className='svg-icon-2' />
                  {qa.label}
                </button>
              ))}
            </div>

            {/* ── Loading ── */}
            {loading && <DashboardGridSkeleton />}

            {/* ── Dashboard Content ── */}
            {!loading && (
              <>
                {/* ROW 1: KPI CARDS — responsive grid */}
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6 mb-5 md:mb-6'>
                  <div className='md:col-span-1 lg:col-span-2 flex flex-col'>
                    <KpiCard title='Active Voyages' value={summaryMetrics[0].value}
                      sub={`Across ${new Set(data.voyages.filter(v => v.active).map(v => v.vessel?.id)).size} active vessels`}
                      onClick={() => openModal('voyageDetail')}
                      meta={kpiMeta.activeVoyages} sparklineColor='#3B82F6'
                      chart={!sourceError('voyages') && data.vessels.length > 0 ? { kind: 'line', values: data.vessels.map(v => activeVoyages.filter(row => Number(row.vessel?.id) === Number(v.id)).length), labels: data.vessels.map(v => v.fleet_name || v.name), caption: 'Active voyages per vessel' } : undefined} />
                  </div>
                  <div className='md:col-span-1 lg:col-span-2 flex flex-col'>
                    <KpiCard title='Vessels At Sea' value={summaryMetrics[1].value}
                      sub={`${kpis.vesselsUnknown} statuses unknown`}
                      onClick={() => openModal('vesselsAtSea')}
                      meta={kpiMeta.vesselsAtSea} sparklineColor='#10B981'
                      chart={{ kind: 'line', values: statusChartVessels.map(v => Number(vesselStatusRows.some(row => row.vesselId === v.id && row.status === 'At Sea'))), labels: statusChartVessels.map(v => v.fleet_name || v.name), caption: 'At sea by active-voyage vessel (1=yes, 0=no)' }} />
                  </div>
                  <div className='md:col-span-1 lg:col-span-2 flex flex-col'>
                    <KpiCard title='Vessels In Port' value={summaryMetrics[2].value}
                      sub={`${kpis.vesselsAtAnchor} at anchorage (separate)`}
                      onClick={() => openModal('vesselsInPort')}
                      meta={kpiMeta.vesselsInPort} sparklineColor='#3B82F6'
                      chart={{ kind: 'line', values: statusChartVessels.map(v => Number(vesselStatusRows.some(row => row.vesselId === v.id && row.status === 'In Port'))), labels: statusChartVessels.map(v => v.fleet_name || v.name), caption: 'In port by active-voyage vessel (1=yes, 0=no)' }} />
                  </div>
                  <div className='md:col-span-1 lg:col-span-2 flex flex-col'>
                    <KpiCard title='Open Defects' value={summaryMetrics[3].value}
                      sub='Excludes Drafts and closed/reviewed'
                      onClick={() => openModal('defectDetail')}
                      meta={kpiMeta.openDefects} sparklineColor='#EF4444' barMode
                      chart={{ kind: 'bars', values: ['SUBMIT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED'].map(status => data.defects.filter(d => isOpenDashboardDefect(d) && d.status === status).length), labels: ['Submitted', 'Pending approval', 'Approved', 'Rejected'], caption: 'Open defects by status' }} />
                  </div>
                  <div className='md:col-span-1 lg:col-span-2 flex flex-col'>
                    <KpiCard title='Final Calculated CII Rating' value={summaryMetrics[4].value}
                      sub={summaryMetrics[4].detail || `${ciiYear} · Select a vessel to view its rating`}
                      onClick={() => openModal('ciiDetail')}
                      meta={kpiMeta.ciiRating} gaugeMode
                      controls={<select aria-label='CII reporting year' className='form-select form-select-sm mb-2 py-1' style={{ fontSize: '10px' }} value={ciiYear} onChange={e => setCiiYear(Number(e.target.value))} disabled={loading || finalCii.loading}>
                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => <option key={year} value={year}>{year}</option>)}
                      </select>} />
                  </div>
                  <div className='md:col-span-1 lg:col-span-2 flex flex-col'>
                    <KpiCard title='DPR Reports' value={summaryMetrics[5].value}
                      sub='Draft Daily Position Reports'
                      onClick={() => openModal('dprDrafts')}
                      meta={kpiMeta.draftDprReports} sparklineColor='#F59E0B'
                      chart={{ kind: 'line', values: draftDates.map(date => data.dprList.filter(d => d.status === 'DRAFT' && d.reportDate?.startsWith(date)).length), labels: draftDates, caption: 'Drafts by report date · 7 days (UTC)' }} />
                  </div>
                </div>

                {/* ROW 2: VESSEL STATUS + VOYAGE SCHEDULE */}
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6 mb-5 md:mb-6'>
                  <div className='md:col-span-1 lg:col-span-8 flex flex-col'>
                    {!sourceError('status') ? <VesselStatusTable
                      rows={vesselStatusRows}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      onRowClick={row => { openModal('voyageDetail'); setSelectedVoyageId(row.voyageId) }}
                    /> : <div className='alert alert-warning'>Vessel status unavailable. {sourceError('status')}</div>}
                  </div>
                  <div className='md:col-span-1 lg:col-span-4 flex flex-col'>
                    {!sourceError('voyages') ? <VoyageTimeline activeVoyages={data.voyages.filter(v => v.active)} showMissingEta={data.vessels.length === 1} /> : <div className='alert alert-warning'>Voyage schedule unavailable. {sourceError('voyages')}</div>}
                  </div>
                </div>

                {/* ROW 3: WORK QUEUE + CARGO + DPR + OPTIONAL CII */}
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6 mb-5 md:mb-6'>
                  <div className={`md:col-span-1 ${dashboardCardVisibility.ciiRatings ? 'lg:col-span-3' : 'lg:col-span-4'} flex flex-col`}>
                    {!sourceError('reports') && !sourceError('defects') ? <WorkQueue createdReports={data.createdReports} defects={data.defects}
                      onViewAll={() => openModal('workQueue')} /> : <div className='alert alert-warning'>Work queue unavailable. {sourceError('reports') || sourceError('defects')}</div>}
                  </div>
                  <div className={`md:col-span-1 ${dashboardCardVisibility.ciiRatings ? 'lg:col-span-3' : 'lg:col-span-4'} flex flex-col`}>
                    {!sourceError('cargo') ? <CargoOperations cargoOps={data.cargoOps} activeCargoOps={kpis.activeCargoOps}
                      tankCargoCount={kpis.tankCargoCount} holdCargoCount={kpis.holdCargoCount}
                      onViewAll={() => openModal('cargoDetail')} /> : <div className='alert alert-warning'>Cargo data unavailable. {sourceError('cargo')}</div>}
                  </div>
                  <div className={`md:col-span-1 ${dashboardCardVisibility.ciiRatings ? 'lg:col-span-3' : 'lg:col-span-4'} flex flex-col`}>
                    {!sourceError('dpr') ? <DprSummary dprList={data.dprList} totalDprToday={kpis.totalDprToday}
                      dprOverdue={kpis.dprOverdue}
                      onViewAll={() => openModal('dprDetail')} /> : <div className='alert alert-warning'>Daily position reports unavailable. {sourceError('dpr')}</div>}
                  </div>
                  {dashboardCardVisibility.ciiRatings && <div className='md:col-span-1 lg:col-span-3 flex flex-col'>
                    {!sourceError('cii') ? <CiiPanel ciiRatingDistribution={kpis.ciiRatingDistribution} ciiRating={kpis.ciiRating}
                      onViewAll={() => openModal('kpiDetail')} /> : <div className='alert alert-warning'>Recorded CII unavailable. {sourceError('cii')}</div>}
                  </div>}
                </div>

                {/* ROW 4: DEFECTS + CONSUMPTION + EVENTS + INSIGHTS */}
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6 mb-5 md:mb-6'>
                  <div className={`md:col-span-1 ${dashboardCardVisibility.inPortConsumption || dashboardCardVisibility.criticalEvents ? 'lg:col-span-3' : 'lg:col-span-6'} flex flex-col`}>
                    {!sourceError('defects') ? <DefectsOverview defects={data.defects} openDefects={kpis.openDefects}
                      overdueDefects={kpis.overdueDefects}
                      onOverdue={() => { openModal('defectDetail'); setDefectView('overdue') }}
                      onViewAll={() => openModal('defectDetail')} /> : <div className='alert alert-warning'>Defect data unavailable. {sourceError('defects')}</div>}
                  </div>
                  {dashboardCardVisibility.inPortConsumption && <div className='md:col-span-1 lg:col-span-3 flex flex-col'>
                    {!sourceError('dpr') ? <ConsumptionTable rows={portConsumption}
                      onViewAll={() => openModal('inPortConsumption')} /> : <div className='alert alert-warning'>In-port consumption unavailable. {sourceError('dpr')}</div>}
                  </div>}
                  {dashboardCardVisibility.criticalEvents && <div className='md:col-span-1 lg:col-span-3 flex flex-col'>
                    {!sourceError('voyages') ? <CriticalEvents activeVoyages={data.voyages.filter(v => v.active)}
                      onViewAll={() => openModal('criticalEvents')} /> : <div className='alert alert-warning'>Voyage events unavailable. {sourceError('voyages')}</div>}
                  </div>}
                  <div className={`md:col-span-1 ${dashboardCardVisibility.inPortConsumption || dashboardCardVisibility.criticalEvents ? 'lg:col-span-3' : 'lg:col-span-6'} flex flex-col`}>
                    <InsightsPanel insights={insights} onViewAll={() => openModal('insights')} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ─── Modals ─── */}
      <VoyageDetailModal key={`${locationStatus || 'active'}-${selectedVoyageId ?? 'all'}`} open={['voyageDetail', 'vesselsAtSea', 'vesselsInPort'].includes(activeModal || '')}
        onClose={closeModal} voyages={detailVoyages} title={locationStatus ? `Vessels ${locationStatus}` : 'Active Voyages'} locationStatus={locationStatus} />
      <DefectDetailModal key={defectView} open={activeModal === 'defectDetail'} onClose={closeModal}
        title={defectView === 'overdue' ? 'Defect Overdue' : 'Open Defect Reports'}
        overdue={defectView === 'overdue'}
        defects={data.defects.filter(d => defectView === 'overdue' ? isOverdueDashboardDefect(d) : isOpenDashboardDefect(d))} />
      <DprDetailModal key={activeModal === 'dprDrafts' ? 'drafts' : 'all'} open={activeModal === 'dprDetail' || activeModal === 'dprDrafts'} onClose={closeModal}
        title={activeModal === 'dprDrafts' ? 'DPR Reports — Drafts' : 'DPR Summary'}
        dprList={activeModal === 'dprDrafts' ? data.dprList.filter(d => d.status === 'DRAFT') : data.dprList} />
      <PortConsumptionModal open={activeModal === 'inPortConsumption'} onClose={closeModal} rows={portConsumption} />
      <CargoDetailModal open={activeModal === 'cargoDetail'} onClose={closeModal} cargoOps={data.cargoOps} />
      <WorkQueueModal open={activeModal === 'workQueue'} onClose={closeModal} createdReports={data.createdReports} defects={data.defects} />
      <CriticalEventsModal open={activeModal === 'criticalEvents'} onClose={closeModal} voyages={data.voyages} onViewVoyage={id => { openModal('voyageDetail'); setSelectedVoyageId(id) }} />
      <InsightsModal open={activeModal === 'insights'} onClose={closeModal} insights={insights} />
      <KpiDetailModal open={activeModal === 'kpiDetail' || activeModal === 'ciiDetail'} onClose={closeModal} kpis={kpis}
        title={activeModal === 'ciiDetail' ? 'Final Calculated CII Rating' : undefined}
        destination={activeModal === 'ciiDetail' ? { label: 'Go to CII Summary', path: '/operations/reports/cii-summary' } : summaryDestination}
        metrics={activeModal === 'ciiDetail' ? [summaryMetrics[4]] : shortcutMetrics}
        canDownload={activeModal === 'ciiDetail' ? permissionsLoaded && opCan(OP.READ_CII_SUMMARY) : summaryCanDownload} />
    </div>
  )
}

export default OperationsDashboard
